/**
 * recommend.js — turns a Project / Brand Intake into a document plan.
 *
 * Deterministic and explainable. No model call, no scoring heuristics: each rule states a
 * condition, the documents it adds, the level, and the reason the founder sees ("because you use a
 * co-packer"). The same answers always produce the same plan, and every line can be traced to the
 * answer that caused it.
 *
 * Levels
 *   required     — you are already doing the thing this document governs
 *   recommended  — you are about to, or the risk of going without is high
 *   situational  — worth knowing about; only some brands need it
 *
 * When several rules add the same document, the strongest level wins and the reasons merge.
 */

import { DOC_BY_ID } from "../../data/agreements/documentLibrary.js";
import { STAGES } from "../../data/agreements/stages.js";

export const LEVELS = ["required", "recommended", "situational"];
const LEVEL_RANK = { required: 0, recommended: 1, situational: 2 };

const has = (v, key) => Array.isArray(v) && v.includes(key);
const isAny = (v, ...keys) => keys.some((k) => has(v, k));
const stageIndex = (id) => STAGES.findIndex((s) => s.id === id);
const atOrPast = (intake, stageId) => stageIndex(intake.stage) >= stageIndex(stageId);

/**
 * The rule set. Order does not affect the outcome — merging is level-based — but rules are grouped
 * to stay readable.
 */
export const RULES = [
  // ── Foundation: everyone ────────────────────────────────────────────────
  {
    id: "always-nda",
    when: () => true,
    because: "Every brand shares something confidential before it is protected",
    docs: [
      ["mutual-nda", "required"],
      ["one-way-nda", "recommended"],
    ],
  },
  {
    id: "always-trademark",
    when: () => true,
    because: "Your brand name and artwork have to end up owned by the company",
    docs: [
      ["trademark-assignment", "recommended"],
      ["ip-assignment-agreement", "required"],
    ],
  },

  // ── Entity & ownership ──────────────────────────────────────────────────
  {
    id: "not-formed",
    when: (a) => a.entityType === "none",
    because: "You have not formed an entity yet",
    docs: [
      ["founder-agreement", "required"],
      ["llc-operating-agreement", "recommended"],
      ["corporate-bylaws", "situational"],
    ],
  },
  {
    id: "llc",
    when: (a) => a.entityType === "llc",
    because: "You are an LLC",
    docs: [
      ["llc-operating-agreement", "required"],
      ["written-consent-of-members", "recommended"],
    ],
  },
  {
    id: "llc-solo",
    when: (a) => a.entityType === "llc" && a.founderCount === "solo",
    because: "You are a single-member LLC",
    docs: [["single-member-llc-operating-agreement", "required"]],
  },
  {
    id: "llc-multi",
    when: (a) => a.entityType === "llc" && a.founderCount === "two-plus",
    because: "You are a multi-member LLC",
    docs: [
      ["multi-member-llc-operating-agreement", "required"],
      ["buy-sell-agreement", "recommended"],
    ],
  },
  {
    id: "corp",
    when: (a) => a.entityType === "corporation",
    because: "You are a corporation",
    docs: [
      ["corporate-bylaws", "required"],
      ["board-consent", "required"],
      ["officer-appointment-resolution", "recommended"],
      ["board-resolution", "recommended"],
      ["written-consent-of-shareholders", "recommended"],
      ["indemnification-agreement", "situational"],
    ],
  },
  {
    id: "corp-founders",
    when: (a) => a.entityType === "corporation" && a.founderCount === "two-plus",
    because: "Multiple founders in a corporation",
    docs: [
      ["shareholder-agreement", "required"],
      ["founder-stock-purchase-agreement", "required"],
      ["right-of-first-refusal-agreement", "recommended"],
      ["stock-transfer-restriction-agreement", "recommended"],
    ],
  },
  {
    id: "cofounders",
    when: (a) => a.founderCount === "two-plus",
    because: "You have co-founders",
    docs: [
      ["co-founder-agreement", "required"],
      ["founder-vesting-agreement", "required"],
      ["vesting-agreement", "recommended"],
    ],
  },
  {
    id: "founder-ip-gap",
    when: (a) => a.founderIpAssigned === "no" || a.founderIpAssigned === "unsure",
    because: "Founder IP has not been confirmed as assigned to the company",
    docs: [
      ["founder-ip-assignment", "required"],
      ["founder-invention-assignment", "required"],
      ["piia", "recommended"],
    ],
  },
  {
    id: "equity-verbal",
    when: (a) => a.equityPromised === "verbally",
    because: "Equity has been promised but not documented",
    docs: [
      ["restricted-stock-purchase-agreement", "required"],
      ["advisor-equity-agreement", "recommended"],
      ["cap-table-documentation", "required"],
    ],
  },
  {
    id: "equity-documented",
    when: (a) => a.equityPromised === "documented",
    because: "You have issued equity",
    docs: [
      ["cap-table-documentation", "recommended"],
      ["equity-incentive-plan", "situational"],
      ["stock-option-agreement", "situational"],
    ],
  },

  // ── People ──────────────────────────────────────────────────────────────
  {
    id: "employees",
    when: (a) => has(a.team, "employees"),
    because: "You have employees",
    docs: [
      ["employment-agreement", "required"],
      ["offer-letter", "required"],
      ["employee-confidentiality-agreement", "required"],
      ["employee-ip-assignment-agreement", "required"],
      ["employee-nda", "recommended"],
      ["separation-agreement", "situational"],
    ],
  },
  {
    id: "contractors",
    when: (a) => has(a.team, "contractors"),
    because: "You work with independent contractors",
    docs: [
      ["independent-contractor-agreement", "required"],
      ["contractor-nda", "required"],
      ["contractor-work-for-hire-agreement", "required"],
      ["work-for-hire-agreement", "recommended"],
      ["contractor-termination-agreement", "situational"],
    ],
  },
  {
    id: "consultants",
    when: (a) => has(a.team, "consultants"),
    because: "You work with consultants",
    docs: [
      ["consultant-agreement", "required"],
      ["contractor-nda", "recommended"],
    ],
  },
  {
    id: "advisors",
    when: (a) => has(a.team, "advisors"),
    because: "You have advisors",
    docs: [
      ["advisor-agreement", "required"],
      ["advisory-board-agreement", "recommended"],
      ["advisor-equity-agreement", "recommended"],
    ],
  },
  {
    id: "sales-reps",
    when: (a) => has(a.team, "sales-reps"),
    because: "You have commission sales reps",
    docs: [
      ["independent-sales-rep-agreement", "required"],
      ["commission-agreement", "required"],
      ["sales-employee-compensation-agreement", "situational"],
    ],
  },
  {
    id: "demo-staff",
    when: (a) => has(a.team, "demo-staff"),
    because: "You use demo or sampling staff",
    docs: [
      ["demo-contractor-agreement", "required"],
      ["independent-contractor-agreement", "recommended"],
    ],
  },

  // ── Product development & formula ───────────────────────────────────────
  {
    id: "formulator",
    when: (a) => isAny(a.formulationBy, "food-scientist", "lab"),
    because: "An outside formulator or lab develops your product",
    docs: [
      ["product-development-agreement", "required"],
      ["product-development-nda", "required"],
      ["formula-development-agreement", "required"],
      ["formula-ownership-agreement", "required"],
      ["food-scientist-agreement", "recommended"],
      ["product-development-lab-agreement", "recommended"],
      ["product-development-ip-agreement", "recommended"],
    ],
  },
  {
    id: "flavor-house",
    when: (a) => has(a.formulationBy, "flavor-house"),
    because: "A flavor house is involved in your formula",
    docs: [
      ["flavor-house-agreement", "required"],
      ["formula-ownership-agreement", "required"],
      ["recipe-formula-confidentiality-agreement", "recommended"],
      ["trade-secret-protection-agreement", "recommended"],
    ],
  },
  {
    id: "copacker-formulates",
    when: (a) => has(a.formulationBy, "co-packer"),
    because: "Your co-packer develops or modifies the formula",
    docs: [
      ["formula-ownership-agreement", "required"],
      ["formula-assignment-agreement", "required"],
      ["product-development-ip-agreement", "required"],
      ["manufacturing-nda", "required"],
    ],
  },
  {
    id: "formula-unowned",
    when: (a) => a.formulaOwnership === "no" || a.formulaOwnership === "unsure",
    because: "Formula ownership is not confirmed in writing",
    docs: [
      ["formula-ownership-agreement", "required"],
      ["formula-assignment-agreement", "required"],
      ["trade-secret-protection-agreement", "recommended"],
    ],
  },
  {
    id: "testing",
    when: (a) => atOrPast(a, "development") && a.formulationBy?.length && !has(a.formulationBy, "not-started"),
    because: "You are developing a product that has to hold up on a shelf",
    docs: [
      ["stability-testing-agreement", "recommended"],
      ["shelf-life-testing-agreement", "recommended"],
      ["sample-testing-agreement", "situational"],
      ["sensory-testing-agreement", "situational"],
      ["product-testing-participant-release", "situational"],
    ],
  },

  // ── Manufacturing ───────────────────────────────────────────────────────
  {
    id: "copacker",
    when: (a) => a.manufacturingModel === "co-packer",
    because: "You use a third-party co-packer",
    docs: [
      ["co-packer-agreement", "required"],
      ["manufacturing-nda", "required"],
      ["quality-agreement", "required"],
      ["product-specification-sheet", "required"],
      ["recall-responsibility-agreement", "required"],
      ["co-packer-nda", "recommended"],
      ["manufacturing-change-control-agreement", "recommended"],
      ["food-safety-agreement", "recommended"],
      ["pilot-run-agreement", "situational"],
    ],
  },
  {
    id: "contract-manufacturer",
    when: (a) => a.manufacturingModel === "contract-manufacturer",
    because: "You use a contract manufacturer",
    docs: [
      ["contract-manufacturing-agreement", "required"],
      ["manufacturing-services-agreement", "recommended"],
      ["quality-agreement", "required"],
      ["product-specification-sheet", "required"],
      ["recall-responsibility-agreement", "required"],
      ["manufacturing-nda", "required"],
    ],
  },
  {
    id: "private-label",
    when: (a) => a.manufacturingModel === "private-label",
    because: "You use private label manufacturing",
    docs: [
      ["private-label-manufacturing-agreement", "required"],
      ["quality-agreement", "required"],
      ["specification-agreement", "recommended"],
      ["recall-responsibility-agreement", "required"],
    ],
  },
  {
    id: "own-facility",
    when: (a) => a.manufacturingModel === "own-facility",
    because: "You manufacture in your own facility",
    docs: [
      ["food-safety-agreement", "recommended"],
      ["supplier-quality-agreement", "required"],
      ["quality-assurance-agreement", "recommended"],
      ["recall-responsibility-agreement", "recommended"],
    ],
  },
  {
    id: "toll",
    when: (a) => a.suppliesInputs === "we-do" && a.manufacturingModel !== "not-yet",
    because: "You supply the inputs and the manufacturer only runs the line",
    docs: [
      ["toll-manufacturing-agreement", "recommended"],
      ["production-agreement", "situational"],
    ],
  },

  // ── Suppliers & packaging ───────────────────────────────────────────────
  {
    id: "buys-inputs",
    when: (a) => a.suppliesInputs === "we-do" || a.suppliesInputs === "mixed",
    because: "You buy ingredients or packaging directly",
    docs: [
      ["ingredient-supply-agreement", "required"],
      ["raw-material-supply-agreement", "recommended"],
      ["master-supply-agreement", "recommended"],
      ["purchase-order-terms", "required"],
      ["supplier-quality-agreement", "recommended"],
      ["supplier-nda", "situational"],
      ["vendor-agreement", "situational"],
      ["purchase-agreement", "situational"],
    ],
  },
  {
    id: "packaging",
    when: (a) => (a.packagingTypes || []).length > 0,
    because: "You buy packaging with long lead times and large minimums",
    docs: [
      ["packaging-supply-agreement", "required"],
      ["packaging-specification-agreement", "recommended"],
      ["packaging-artwork-ownership", "required"],
    ],
  },
  { id: "cans", when: (a) => has(a.packagingTypes, "cans"), because: "You package in cans", docs: [["can-supply-agreement", "recommended"]] },
  { id: "bottles", when: (a) => has(a.packagingTypes, "bottles"), because: "You package in bottles", docs: [["bottle-supply-agreement", "recommended"]] },
  { id: "labels", when: (a) => has(a.packagingTypes, "labels"), because: "You buy printed labels", docs: [["label-supply-agreement", "recommended"]] },
  { id: "cartons", when: (a) => isAny(a.packagingTypes, "cartons", "boxes"), because: "You buy cartons or cases", docs: [["carton-supply-agreement", "recommended"]] },

  // ── Logistics ───────────────────────────────────────────────────────────
  {
    id: "3pl",
    when: (a) => a.logistics === "3pl",
    because: "Your inventory sits with a 3PL",
    docs: [
      ["three-pl-agreement", "required"],
      ["logistics-services-agreement", "recommended"],
      ["pick-and-pack-agreement", "situational"],
    ],
  },
  {
    id: "warehouse",
    when: (a) => a.logistics === "warehouse",
    because: "You rent warehouse space",
    docs: [
      ["warehouse-agreement", "required"],
      ["transportation-agreement", "recommended"],
    ],
  },
  {
    id: "freight",
    when: (a) => a.logistics !== "none" && atOrPast(a, "launch"),
    because: "You are shipping product",
    docs: [
      ["transportation-agreement", "recommended"],
      ["freight-broker-agreement", "situational"],
      ["carrier-agreement", "situational"],
    ],
  },
  {
    id: "cold-chain",
    when: (a) => a.coldChain === "yes" || a.coldChain === "seasonal",
    because: "Your product is temperature-sensitive",
    docs: [
      ["three-pl-agreement", "recommended"],
      ["warehouse-agreement", "recommended"],
      ["shipping-policy", "recommended"],
    ],
  },

  // ── Channels ────────────────────────────────────────────────────────────
  {
    id: "dtc",
    when: (a) => isAny(a.channels, "dtc", "amazon"),
    because: "You sell online",
    docs: [
      ["website-terms-of-use", "required"],
      ["terms-and-conditions", "required"],
      ["privacy-policy", "required"],
      ["shipping-policy", "required"],
      ["refund-policy", "required"],
      ["cookie-policy", "recommended"],
      ["email-marketing-consent", "recommended"],
      ["sms-marketing-terms", "situational"],
    ],
  },
  {
    id: "subscription",
    when: (a) => has(a.channels, "subscription"),
    because: "You run a subscription program",
    docs: [
      ["subscription-terms", "required"],
      ["auto-renewal-terms", "required"],
      ["refund-policy", "required"],
    ],
  },
  {
    id: "ecommerce-fulfillment",
    when: (a) => isAny(a.channels, "dtc", "amazon", "subscription") && a.logistics !== "none",
    because: "You fulfil online orders",
    docs: [["ecommerce-fulfillment-agreement", "recommended"], ["fulfillment-agreement", "recommended"]],
  },
  {
    id: "retail-direct",
    when: (a) => has(a.channels, "retail-direct"),
    because: "You sell direct to retailers",
    docs: [
      ["retail-vendor-agreement", "required"],
      ["wholesale-supply-agreement", "required"],
      ["retailer-supply-agreement", "recommended"],
      ["promotional-allowance-agreement", "recommended"],
      ["slotting-placement-agreement", "situational"],
      ["retail-marketing-agreement", "situational"],
      ["consignment-agreement", "situational"],
      ["sale-or-return-agreement", "situational"],
    ],
  },
  {
    id: "distributor",
    when: (a) => has(a.channels, "distributor"),
    because: "You sell through a distributor",
    docs: [
      ["wholesale-distribution-agreement", "required"],
      ["territory-distribution-agreement", "recommended"],
      ["non-exclusive-distribution-agreement", "recommended"],
      ["master-distributor-agreement", "situational"],
      ["sub-distributor-agreement", "situational"],
    ],
  },
  {
    id: "distributor-beverage",
    when: (a) => has(a.channels, "distributor") && ["beverage", "energy-drink", "functional-beverage"].includes(a.productCategory),
    because: "Beverage distribution has its own economics and exit problems",
    docs: [["beverage-distributor-agreement", "required"]],
  },
  {
    id: "distributor-food",
    when: (a) => has(a.channels, "distributor") && ["food", "snack", "candy", "supplement", "wellness", "beauty", "other"].includes(a.productCategory),
    because: "You distribute a food or packaged goods product",
    docs: [["food-distributor-agreement", "required"]],
  },
  {
    id: "dsd",
    when: (a) => has(a.channels, "dsd"),
    because: "You use direct store delivery",
    docs: [["dsd-distributor-agreement", "required"]],
  },
  {
    id: "exclusivity",
    when: (a) => a.exclusivityAsked === "asked" || a.exclusivityAsked === "signed",
    because: "Exclusivity is on the table",
    docs: [
      ["exclusive-distribution-agreement", "required"],
      ["territory-distribution-agreement", "required"],
      ["notice-to-cure", "recommended"],
      ["notice-of-termination", "recommended"],
    ],
  },
  {
    id: "broker",
    when: (a) => has(a.channels, "broker"),
    because: "You use a sales broker",
    docs: [
      ["cpg-broker-agreement", "required"],
      ["retail-broker-agreement", "recommended"],
      ["commission-sales-agreement", "required"],
      ["key-account-sales-agreement", "situational"],
      ["broker-plus-distributor-agreement", "situational"],
      ["broker-distributor-hybrid-agreement", "situational"],
    ],
  },
  {
    id: "foodservice",
    when: (a) => has(a.channels, "foodservice"),
    because: "You sell into foodservice",
    docs: [["foodservice-broker-agreement", "required"]],
  },
  {
    id: "international",
    when: (a) => has(a.channels, "international"),
    because: "You sell internationally",
    docs: [
      ["international-distributor-agreement", "required"],
      ["international-sales-agent-agreement", "recommended"],
      ["importer-agreement", "recommended"],
      ["export-agent-agreement", "situational"],
      ["international-supply-agreement", "situational"],
      ["international-trademark-license-agreement", "recommended"],
      ["international-sales-rep-agreement", "situational"],
    ],
  },
  {
    id: "international-manufacturing",
    when: (a) => has(a.channels, "international") && a.manufacturingModel !== "not-yet",
    because: "Cross-border manufacturing or supply",
    docs: [["international-manufacturing-agreement", "situational"]],
  },

  // ── Marketing & creative ────────────────────────────────────────────────
  {
    id: "agency",
    when: (a) => has(a.marketingHelp, "agency"),
    because: "You work with a marketing or ad agency",
    docs: [
      ["marketing-agency-agreement", "required"],
      ["advertising-agency-agreement", "recommended"],
      ["branding-agency-agreement", "situational"],
      ["social-media-management-agreement", "situational"],
    ],
  },
  {
    id: "designer",
    when: (a) => has(a.marketingHelp, "designer"),
    because: "A designer makes your packaging or graphics",
    docs: [
      ["packaging-design-agreement", "required"],
      ["graphic-design-agreement", "recommended"],
      ["design-ownership-agreement", "required"],
      ["packaging-artwork-ownership", "required"],
    ],
  },
  {
    id: "photographer",
    when: (a) => has(a.marketingHelp, "photographer"),
    because: "You commission photography or video",
    docs: [
      ["photography-agreement", "required"],
      ["videography-agreement", "recommended"],
      ["content-release", "required"],
      ["model-release", "recommended"],
    ],
  },
  {
    id: "influencers",
    when: (a) => isAny(a.marketingHelp, "influencers", "ugc"),
    because: "You work with creators",
    docs: [
      ["influencer-agreement", "required"],
      ["ugc-creator-agreement", "required"],
      ["content-creator-agreement", "recommended"],
      ["influencer-disclosure", "required"],
      ["nil-release", "situational"],
    ],
  },
  {
    id: "ambassadors",
    when: (a) => has(a.marketingHelp, "ambassadors"),
    because: "You have brand ambassadors",
    docs: [
      ["ambassador-agreement", "required"],
      ["brand-ambassador-agreement", "recommended"],
      ["event-brand-ambassador-agreement", "situational"],
    ],
  },
  {
    id: "affiliates",
    when: (a) => has(a.marketingHelp, "affiliates"),
    because: "You run an affiliate program",
    docs: [
      ["affiliate-agreement", "required"],
      ["affiliate-disclosure", "required"],
      ["influencer-affiliate-agreement", "recommended"],
      ["referral-agreement", "situational"],
      ["commission-referral-agreement", "situational"],
      ["strategic-referral-partner-agreement", "situational"],
    ],
  },
  {
    id: "creative-unowned",
    when: (a) => a.creativeOwnership === "no" || a.creativeOwnership === "partial" || a.creativeOwnership === "unsure",
    because: "Ownership of your creative assets is not confirmed",
    docs: [
      ["work-for-hire-agreement", "required"],
      ["copyright-assignment", "required"],
      ["design-ownership-agreement", "required"],
      ["ip-assignment-agreement", "required"],
    ],
  },

  // ── Licensing ───────────────────────────────────────────────────────────
  {
    id: "license-inbound",
    when: (a) => has(a.licensing, "inbound"),
    because: "You license someone else's brand or name",
    docs: [
      ["trademark-license-agreement", "required"],
      ["brand-license-agreement", "required"],
      ["character-license-agreement", "situational"],
    ],
  },
  {
    id: "license-outbound",
    when: (a) => has(a.licensing, "outbound"),
    because: "You license your brand to someone else",
    docs: [
      ["trademark-license-agreement", "required"],
      ["brand-license-agreement", "required"],
    ],
  },
  {
    id: "license-celebrity",
    when: (a) => has(a.licensing, "celebrity"),
    because: "A celebrity, athlete or personality is involved",
    docs: [
      ["celebrity-license-agreement", "required"],
      ["nil-agreement", "required"],
      ["nil-release", "required"],
      ["sponsorship-agreement", "situational"],
    ],
  },
  {
    id: "cobrand",
    when: (a) => has(a.licensing, "cobrand"),
    because: "You have a co-branded product",
    docs: [
      ["co-branding-agreement", "required"],
      ["collaboration-agreement", "recommended"],
      ["product-collaboration-agreement", "recommended"],
      ["cross-marketing-agreement", "situational"],
      ["promotional-partnership-agreement", "situational"],
    ],
  },

  // ── Events & promotions ─────────────────────────────────────────────────
  {
    id: "demos",
    when: (a) => has(a.eventsSampling, "demos"),
    because: "You run in-store demos",
    docs: [
      ["demo-agreement", "required"],
      ["demo-contractor-agreement", "required"],
      ["sampling-agreement", "recommended"],
    ],
  },
  {
    id: "sampling",
    when: (a) => has(a.eventsSampling, "sampling"),
    because: "You sample product at events",
    docs: [
      ["event-sampling-agreement", "required"],
      ["product-sampling-release", "required"],
      ["event-participation-agreement", "recommended"],
      ["sampling-agency-agreement", "situational"],
    ],
  },
  {
    id: "sponsorship",
    when: (a) => has(a.eventsSampling, "sponsorship"),
    because: "You sponsor events",
    docs: [
      ["event-sponsorship-agreement", "required"],
      ["event-sponsorship-contract", "required"],
      ["promotional-services-agreement", "situational"],
    ],
  },
  {
    id: "contests",
    when: (a) => has(a.eventsSampling, "contest"),
    because: "You run contests or sweepstakes",
    docs: [
      ["contest-rules", "required"],
      ["sweepstakes-rules", "required"],
      ["product-giveaway-rules", "required"],
      ["promotional-agreement", "situational"],
    ],
  },

  // ── Money ───────────────────────────────────────────────────────────────
  {
    id: "raised-any",
    when: (a) => (a.raised || []).some((v) => v !== "none") ,
    because: "You have taken outside money",
    docs: [
      ["cap-table-documentation", "required"],
      ["founder-stock-documents", "recommended"],
      ["subscription-agreement", "recommended"],
    ],
  },
  {
    id: "raised-safe-note",
    when: (a) => isAny(a.raised, "safe-note", "friends-family", "angels"),
    because: "You raised on SAFEs, notes or informal terms",
    docs: [
      ["safe", "required"],
      ["convertible-note", "required"],
      ["promissory-note-investor", "situational"],
    ],
  },
  {
    id: "raised-priced",
    when: (a) => has(a.raised, "priced"),
    because: "You closed a priced round",
    docs: [
      ["preferred-stock-purchase-agreement", "required"],
      ["investor-rights-agreement", "required"],
      ["voting-agreement", "required"],
      ["rofr-co-sale-agreement", "required"],
      ["stockholders-agreement", "recommended"],
      ["board-observer-agreement", "situational"],
    ],
  },
  {
    id: "raised-strategic",
    when: (a) => has(a.raised, "strategic"),
    because: "A strategic investor is on your cap table",
    docs: [
      ["investor-nda", "recommended"],
      ["stockholders-agreement", "recommended"],
      ["exclusive-distribution-agreement", "situational"],
    ],
  },
  {
    id: "planning-raise",
    when: (a) => a.planningRaise === "yes" || a.planningRaise === "maybe" || a.stage === "fundraising",
    because: "You are raising or about to",
    docs: [
      ["term-sheet", "required"],
      ["investor-letter-of-intent", "recommended"],
      ["cap-table-documentation", "required"],
      ["investor-nda", "situational"],
      ["advisory-equity-agreement", "situational"],
    ],
  },
  {
    id: "debt-bank",
    when: (a) => isAny(a.debt, "bank-loan"),
    because: "You have a bank loan or line of credit",
    docs: [
      ["business-loan-agreement", "required"],
      ["line-of-credit-agreement", "required"],
      ["security-agreement", "required"],
      ["personal-guarantee", "required"],
    ],
  },
  {
    id: "debt-founder",
    when: (a) => has(a.debt, "founder-loan"),
    because: "A founder or shareholder has lent the company money",
    docs: [
      ["founder-loan-agreement", "required"],
      ["shareholder-loan-agreement", "required"],
      ["promissory-note", "required"],
    ],
  },
  {
    id: "debt-equipment",
    when: (a) => has(a.debt, "equipment"),
    because: "You financed equipment",
    docs: [["equipment-financing-agreement", "required"], ["security-agreement", "recommended"]],
  },
  {
    id: "debt-inventory",
    when: (a) => has(a.debt, "inventory"),
    because: "You financed inventory or purchase orders",
    docs: [["inventory-financing-agreement", "required"], ["security-agreement", "recommended"]],
  },
  {
    id: "debt-rbf",
    when: (a) => has(a.debt, "rbf"),
    because: "You use revenue-based financing",
    docs: [["revenue-based-financing-agreement", "required"]],
  },

  // ── Exit ────────────────────────────────────────────────────────────────
  {
    id: "exit-active",
    when: (a) => a.exploringSale === "active" || a.stage === "exit",
    because: "You are exploring a sale",
    docs: [
      ["letter-of-intent", "required"],
      ["due-diligence-nda", "required"],
      ["no-shop-agreement", "required"],
      ["asset-purchase-agreement", "required"],
      ["ma-stock-purchase-agreement", "recommended"],
      ["membership-interest-purchase-agreement", "recommended"],
      ["brand-acquisition-agreement", "recommended"],
      ["ip-purchase-agreement", "required"],
      ["transition-services-agreement", "recommended"],
      ["earn-out-agreement", "situational"],
    ],
  },
  {
    id: "exit-someday",
    when: (a) => a.exploringSale === "someday",
    because: "You want the option to sell later",
    docs: [
      ["due-diligence-nda", "situational"],
      ["letter-of-intent", "situational"],
      ["cap-table-documentation", "recommended"],
    ],
  },

  // ── Disputes: always worth having on the shelf ───────────────────────────
  {
    id: "disputes-baseline",
    when: (a) => atOrPast(a, "launch"),
    because: "You have live commercial relationships that can go wrong",
    docs: [
      ["notice-to-cure", "recommended"],
      ["breach-notice", "recommended"],
      ["notice-of-termination", "recommended"],
      ["demand-letter", "recommended"],
      ["collection-demand", "situational"],
      ["termination-agreement", "situational"],
      ["mutual-termination-agreement", "situational"],
      ["settlement-agreement", "situational"],
      ["mutual-release", "situational"],
      ["general-release", "situational"],
      ["ip-cease-and-desist", "situational"],
      ["confidentiality-breach-notice", "situational"],
    ],
  },
];

/**
 * Build the document plan.
 * @returns {{ items: Array, counts: object, unmatched: string[] }}
 *   items: [{ docId, doc, level, reasons: string[], ruleIds: string[] }] sorted by level then name
 */
export function buildPlan(intake = {}) {
  const acc = new Map();
  const unmatched = [];

  for (const rule of RULES) {
    let fires = false;
    try {
      fires = Boolean(rule.when(intake));
    } catch {
      fires = false;
    }
    if (!fires) continue;

    for (const [docId, level] of rule.docs) {
      const doc = DOC_BY_ID[docId];
      if (!doc) {
        unmatched.push(`${rule.id} -> ${docId}`);
        continue;
      }
      const existing = acc.get(docId);
      if (!existing) {
        acc.set(docId, { docId, doc, level, reasons: [rule.because], ruleIds: [rule.id] });
      } else {
        if (LEVEL_RANK[level] < LEVEL_RANK[existing.level]) existing.level = level;
        if (!existing.reasons.includes(rule.because)) existing.reasons.push(rule.because);
        if (!existing.ruleIds.includes(rule.id)) existing.ruleIds.push(rule.id);
      }
    }
  }

  const items = [...acc.values()].sort(
    (a, b) => LEVEL_RANK[a.level] - LEVEL_RANK[b.level] || a.doc.name.localeCompare(b.doc.name),
  );

  const counts = { required: 0, recommended: 0, situational: 0, total: items.length };
  for (const i of items) counts[i.level] += 1;

  return { items, counts, unmatched };
}

/** Group a plan by the stage each document belongs to, for the checklist view. */
export function planByStage(plan) {
  const out = {};
  for (const s of STAGES) out[s.id] = [];
  for (const item of plan.items) {
    const earliest = item.doc.stages
      .map((s) => STAGES.findIndex((x) => x.id === s))
      .filter((i) => i >= 0)
      .sort((a, b) => a - b)[0];
    const stageId = STAGES[earliest ?? 0]?.id;
    if (out[stageId]) out[stageId].push(item);
  }
  return out;
}

/** Group a plan by library category, for the plan view. */
export function planByCategory(plan) {
  const out = {};
  for (const item of plan.items) {
    (out[item.doc.category] = out[item.doc.category] || []).push(item);
  }
  return out;
}

/** Documents in the plan that carry a drafted template. */
export function planTemplates(plan) {
  return plan.items.filter((i) => Boolean(i.doc.templateId));
}

/** Documents in the plan flagged as attorney-led. */
export function planAttorneyLed(plan) {
  return plan.items.filter((i) => i.doc.attorneyReview === "strongly-recommended");
}

export default { RULES, LEVELS, buildPlan, planByStage, planByCategory, planTemplates, planAttorneyLed };
